<template>
  <div
    class="flex flex-col gap-2 bg-blue-4 p-4 text-darkGray-1 w-full"
    v-if="isEditing"
  >
    <div class="grid grid-cols-3 gap-x-2">
      <label class="col-span-2 mb-1 flex items-center gap-2">
        Periodens start
      </label>
      <input
        type="date"
        class="appearance-none border-2 border-gray-2 block bg-blue-4 text-darkGray-1 placeholder-darkGray-4 rounded-md py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-200"
        :class="{ 'border-2 border-red-2': v$.periodStart.$error }"
        placeholder="Ange periodens start"
        v-model="period.periodStart"
      />
      <span
        v-if="v$.periodStart.$error"
        class="p-1 text-white col-span-full place-self-end text-xs"
        >{{ v$.periodStart.$errors[0].$message }}
      </span>
    </div>
    <div class="grid grid-cols-3 gap-x-2">
      <label class="col-span-2 mb-1 flex items-center gap-2">
        Periodens slut
      </label>
      <input
        type="date"
        class="appearance-none border-2 border-gray-2 block bg-blue-4 text-darkGray-1 placeholder-darkGray-4 rounded-md py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-200"
        :class="{ 'border-2 border-red-2': v$.periodEnd.$error }"
        placeholder="Ange periodens slut"
        v-model="period.periodEnd"
      />
      <span
        v-if="v$.periodEnd.$error"
        class="p-1 text-white col-span-full place-self-end text-xs"
        >{{ v$.periodEnd.$errors[0].$message }}
      </span>
    </div>

    <div class="grid grid-cols-3 gap-x-2">
      <label class="col-span-2 mb-1 flex items-center gap-2">
        Lägsta ålder vid periodens start
      </label>
      <input
        type="number"
        class="appearance-none border-2 border-gray-2 block bg-blue-4 text-darkGray-1 placeholder-darkGray-4 rounded-md py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-200"
        :class="{ 'border-2 border-red-2': v$.ageLow.$error }"
        placeholder="Ange ålder"
        v-model="period.ageLow"
      />
      <span
        v-if="v$.ageLow.$error"
        class="p-1 text-white text-xs col-span-full place-self-end"
        >{{ v$.ageLow.$errors[0].$message }}
      </span>
    </div>
    <div class="grid grid-cols-3 gap-x-2">
      <label class="col-span-2 mb-1 flex items-center gap-2">
        Högsta ålder vid periodens slut
      </label>
      <input
        type="number"
        class="appearance-none border-2 border-gray-2 block bg-blue-4 text-darkGray-1 placeholder-darkGray-4 rounded-md py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-200"
        :class="{ 'border-2 border-red-2': v$.ageHigh.$error }"
        placeholder="Ange ålder"
        v-model="period.ageHigh"
      />
      <span
        v-if="v$.ageHigh.$error"
        class="p-1 text-white col-span-full place-self-end text-xs"
        >{{ v$.ageHigh.$errors[0].$message }}
      </span>
    </div>
    <div class="flex gap-2">
      <button
        @click="() => savePeriod()"
        class="bg-gradient-to-t from-orange-1 from-10% via-orange-1.5 via-45% to-orange-2.5 to-80% disabled:from-gray-1 disabled:to-gray-3 text-white border-none hover:from-orange-1.5 hover:to-orange-4 rounded-md px-3 py-1"
      >
        Spara
      </button>
      <button
        @click="() => removePeriod(props.index)"
        class="bg-gradient-to-t from-red-700 to-red-2 hover:from-red-600 hover:to-red-3 text-sm rounded-md text-white px-3 py-1"
      >
        <i class="fas fa-trash"></i>
      </button>
    </div>
  </div>
  <div v-else>
    <p>
      Minst {{ period.ageLow }} år fyllda {{ period.periodStart }} & Max
      {{ period.ageHigh }} år fyllda {{ period.periodEnd }}.
    </p>
    <div class="flex gap-2">
      <button
        @click="isEditing = !isEditing"
        class="bg-white text-sm rounded-lg text-darkGray-1 px-3 py-1"
      >
        Redigera
      </button>
      <button
        @click="() => removePeriod(props.index)"
        class="bg-red-2 text-sm rounded-lg text-white px-3 py-1"
      >
        Ta bort
      </button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, defineProps, defineEmits, reactive } from "vue";
import useValidate from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";

import { computed } from "@vue/reactivity";

const props = defineProps<{
  period: {
    periodStart: string;
    periodEnd: string;
    ageLow: number;
    ageHigh: number;
  };
  index: number;
}>();

const period = reactive(props.period);

const isEditing = ref(true);

const messages = {
  required: "Fältet är obligatoriskt",
  startDateIsBeforeEndDate: "Datumet kan inte vara före periodens startdatum",
  endDateIsAfterStartDate: "Datumet kan inte vara efter periodens slutdatum",
  ageIsHigherThanMaxAge: "Åldern kan inte vara lägre än maxåldern",
  ageIsLowerThanMinAge: "Åldern kan inte vara högre än minåldern",
  ageIsPositive: "Åldern kan inte vara negativ",
};

const rules = computed(() => {
  return {
    periodStart: {
      required,
      endDateIsAfterStartDate: helpers.withMessage(
        messages.endDateIsAfterStartDate,
        (value: string) => {
          const date = new Date(value);
          const endDate = new Date(period.periodEnd);
          return date <= endDate;
        }
      ),
    },
    periodEnd: {
      required,
      startDateIsBeforeEndDate: helpers.withMessage(
        messages.startDateIsBeforeEndDate,
        (value: string) => {
          const date = new Date(value);
          const startDate = new Date(period.periodStart);
          return date >= startDate;
        }
      ),
    },
    ageLow: {
      required,
      ageIsPositive: helpers.withMessage(
        messages.ageIsPositive,
        (value: number) => {
          return value >= 0;
        }
      ),
      ageIsLowerThanMinAge: helpers.withMessage(
        messages.ageIsLowerThanMinAge,
        (value: number) => {
          return value <= period.ageHigh;
        }
      ),
    },
    ageHigh: {
      required,
      ageIsPositive: helpers.withMessage(
        messages.ageIsPositive,
        (value: number) => {
          return value >= 0;
        }
      ),
      ageIsHigherThanMaxAge: helpers.withMessage(
        messages.ageIsHigherThanMaxAge,
        (value: number) => {
          return value >= period.ageLow;
        }
      ),
    },
  };
});

const v$ = useValidate(rules, period);

const validate = () => {
  v$.value.$validate();
  if (!v$.value.$error) {
    return true;
  } else {
    return false;
  }
};

const emit = defineEmits(["removePeriod", "savePeriod"]);

const removePeriod = (index: number) => {
  emit("removePeriod", index);
};

const savePeriod = () => {
  if (!validate()) {
    return;
  }
  isEditing.value = false;
  emit("savePeriod", period, props.index);
};
</script>
